import type { IOfferPayloadEnterprise, IPutJobOfferPayload } from "@api";
import type { IApply } from "@bbj-types/apply";
import type { IContact } from "@bbj-types/contact";
import type { IJob } from "@bbj-types/job";
import type { IOffer } from "@bbj-types/offer";
import type { ICreateOfferPayloadOffer } from "@bbj-types/offer/create";
import type { IActionBatchCreate } from "@root/api/services/action/batchCreateActions";
import type { IUserMe } from "@root/api/services/users/me";
import { isValidDate } from "@root/utils/date/isValidDate";
import { toStartOfDayISO } from "@root/utils/date/toStartOfDayISO";
import type { TimeZone } from "@styleguide";
import type { IEnterpriseBody } from "../context";
import type { IApplyCreate } from "./";

export const CONTRACT_TYPES_WITHOUT_WORKING_TIME = ["auto_entrepreneur", "benevolat_engagement_interet_general"];

export const requiredForContractType = (offer: Partial<IOffer>) => {
  return offer?.contractType && !CONTRACT_TYPES_WITHOUT_WORKING_TIME.includes(offer.contractType[0]);
};

export const verifyOfferFields = (
  offer: Partial<IOffer>,
  enterprise: IEnterpriseBody | null,
  appellation: IJob | null,
  apply: IApplyCreate,
  errors: Record<string, boolean>,
  applyFollowUp = false,
  createOffer = false
) => {
  const formErrors = { ...errors };
  const labels: string[] = [];
  if (!offer.name) {
    labels.push("Poste");
    formErrors.name = true;
  }
  if (!offer.isPermanent && !isValidDate(offer.expirationDate)) {
    labels.push("Date d'expiration");
    formErrors.expirationDate = true;
  }
  if (offer.startDate && !isValidDate(offer.startDate)) {
    labels.push("Date de début");
    formErrors.startDate = true;
  }
  if (!appellation?.libelle) {
    labels.push("Appellation");
    formErrors.appellation = true;
  }
  if (!offer.externalSource) {
    labels.push("Source");
    formErrors.externalSource = true;
  }
  if (!offer.contractType) {
    labels.push("Type de contrat");
    formErrors.contractType = true;
  }
  if (!applyFollowUp || createOffer) {
    if (!offer.address) {
      labels.push("Adresse");
      formErrors.address = true;
    }
  }
  if (!applyFollowUp || createOffer || apply.status === "success") {
    if (!enterprise?.name) {
      labels.push("Entreprise");
      formErrors.enterprise = true;
    }
  }
  if (apply.status === "waiting_itw" && !apply.itwDate) {
    labels.push("Date entretien");
    formErrors.itwDate = true;
  }
  if (applyFollowUp && apply.status === "success") {
    const hasNoWorkingTime = offer.contractType?.some((contractType) =>
      CONTRACT_TYPES_WITHOUT_WORKING_TIME.includes(contractType)
    );
    const hasNoContractDuration = hasNoWorkingTime || offer.contractType?.includes("cdi");
    if (!hasNoWorkingTime && !offer.workWeeklyDuration) {
      labels.push("Durée hebdo");
      formErrors.workWeeklyDuration = true;
    }
    if (!hasNoContractDuration && !offer.contractDuration?.duration) {
      labels.push("Durée");
      formErrors.contractDurationValue = true;
    }
    if (!hasNoContractDuration && !offer.contractDuration?.unit) {
      labels.push("Unité");
      formErrors.contractDurationUnit = true;
    }
    if (!apply.contractStartDate) {
      labels.push("Date de début de contrat");
      formErrors.contractStartDate = true;
    }
  }

  const message = `Veuillez corriger les informations suivantes : ${labels.slice(0, -1).join(", ")}${
    labels.length > 1 ? " et " : ""
  }${labels.slice(-1)[0]} de l'offre.`;

  return {
    hasError: labels.length,
    formErrors,
    message,
  };
};

export const formatCreateOffer = (offer: IOffer, appellation: IJob): ICreateOfferPayloadOffer => {
  return {
    name: offer.name,
    url: offer.url,
    address: offer.address,
    contractType: offer.contractType,
    contractDuration:
      offer.contractDuration &&
      !offer.contractType?.includes("cdi") &&
      offer.contractDuration.unit &&
      offer.contractDuration.duration
        ? {
            unit: offer.contractDuration.unit,
            duration: offer.contractDuration.duration,
          }
        : undefined,
    workWeeklyDuration: offer.workWeeklyDuration,
    startDate: offer.startDate,
    externalSource: offer.externalSource,
    expirationDate: offer.expirationDate,
    isPermanent: offer.isPermanent,
    description: offer.description,
    appellationCode: appellation?._id,
    appellationLabel: appellation?.libelle,
    romeCode: appellation?.rome._id,
    romeLabel: appellation?.rome.libelle,
  };
};

export const formatPutOffer = (
  offer: IOffer,
  appellation: IJob,
  editorId: string,
  timeZone: TimeZone
): IPutJobOfferPayload["offer"] => {
  const { enterprise, contact, metadata, startDate, expirationDate, ...putOffer } = offer;

  return {
    ...putOffer,
    startDate: toStartOfDayISO(startDate, timeZone),
    expirationDate: toStartOfDayISO(expirationDate, timeZone),
    publicationUpdatedDate: new Date().toISOString(),
    contractDuration:
      offer.contractDuration &&
      !offer.contractType?.includes("cdi") &&
      offer.contractDuration.unit &&
      offer.contractDuration.duration
        ? {
            unit: offer.contractDuration.unit,
            duration: offer.contractDuration.duration,
          }
        : undefined,
    appellationCode: appellation._id,
    appellationLabel: appellation.libelle,
    romeCode: appellation.rome._id,
    romeLabel: appellation.rome.libelle,
    lastEditorId: editorId,
  };
};

export const formatEnterprise = (enterprise: IEnterpriseBody, isSharedEnterprise: boolean): IOfferPayloadEnterprise => {
  const { _id, name } = enterprise;
  return _id ? { _id } : { name, isTemp: !isSharedEnterprise };
};

export const formatContact = (contact: IContact | null): Partial<IContact> | null => {
  if (contact === null) {
    return null;
  }
  const payloadContact: Partial<IContact> = { ...contact };
  for (const key in payloadContact) {
    if (payloadContact[key] === "" || payloadContact[key] === null) {
      delete payloadContact[key];
    }
  }
  return payloadContact;
};

export const isOnConfirmApply = (_onConfirm: unknown, isApply: boolean): _onConfirm is (result: IApply) => void => {
  return isApply;
};

export const isOnConfirmOffer = (_onConfirm: unknown, isOffer: boolean): _onConfirm is (result: IOffer) => void => {
  return isOffer;
};

export const generateJobFollowUpAction = (
  followUpTitle: string,
  followUpDate: Date,
  apply: IApply,
  user: IUserMe
): IActionBatchCreate => ({
  owner: user._id,
  apply: apply._id,
  candidate: typeof apply.candidate === "string" ? apply.candidate : apply.candidate._id,
  type: "job-follow-up",
  status: "todo",
  title: followUpTitle,
  isAutoGenerate: false,
  deadlineDate: followUpDate,
});

export const getApplyContractEndDate = (
  startDate: string | undefined,
  contractType: string[] | undefined,
  duration: IOffer["contractDuration"]
): string | undefined => {
  if (
    !startDate ||
    !duration ||
    !duration.unit ||
    !duration.duration ||
    !contractType ||
    contractType.includes("cdi")
  ) {
    return undefined;
  }
  const contractEndDate = new Date(startDate);
  switch (duration.unit) {
    case "day":
      contractEndDate.setDate(contractEndDate.getDate() + duration.duration);
      break;
    case "week":
      contractEndDate.setDate(contractEndDate.getDate() + duration.duration * 7);
      break;
    case "month":
      contractEndDate.setMonth(contractEndDate.getMonth() + duration.duration);
      break;
    case "year":
      contractEndDate.setFullYear(contractEndDate.getFullYear() + duration.duration);
      break;
  }
  contractEndDate.setDate(contractEndDate.getDate() - 1);
  return contractEndDate.toISOString();
};
